import { useFetcher } from "@website/fetcher/use-fetcher";
import {
  useJwtToken,
  useLoginStatus,
  useUserInfo
} from "@website/hooks/auth-manager";
import { BreadcrumbItemType } from "@website/ui/breadcrumb";
import { useSnackbar } from "@website/ui/snackbar";
import { getChannel } from "@website/utils/channel/get-channel";
import { getOrigin } from "@website/utils/channel/get-origin";
import { isChannel } from "@website/utils/channel/helpers";
import { isInvalidCheckin, today } from "@website/utils/date-format";
import { getImage } from "@website/utils/get-image";
import { numberConvert } from "@website/utils/number-convert";
import { numberSeparator } from "@website/utils/number-separator";
import { useSnappPro } from "@website/widget/header/hooks/use-snapp-pro";
import { OnVisibleCommentType } from "@website/widget/reserve-comment";
import { ReserveMapPropertiesTypes } from "@website/widget/reserve-map";
import debounce from "lodash-es/debounce";
import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import omit from "lodash-es/omit";
import pick from "lodash-es/pick";
import toString from "lodash-es/toString";
import uniqBy from "lodash-es/uniqBy";
import { useRouter } from "next/router";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useRoomCalendar } from "./calendar.hooks";
import { useHotelComments } from "./comments.hook";
import { useHotelCreateBook } from "./createBook.hook";
import {
  fetchAddRoomNotify,
  fetchHotelRoomsData,
  fetchInstalmentWallet,
  fetchRemoveRoomNotify,
  fetchRoomsNotify,
  fetchUsefulnessReviewData
} from "./index.server";
import { useHotelPageTracking } from "./index.tracking";
import { HotelPagePropertiesType } from "./index.types";
import { useHotelRoomComments } from "./roomComments.hook";
import { useHotelSearch } from "./search.hook";

export const useHotelPageDataTransform = ({
  hotelData,
  seoData,
  hotelRateData,
  cancellationPoliciesData,
  hotelPriceRange,
  hotelRecommendations
}: HotelPagePropertiesType) => {
  const router = useRouter();
  const searchProperties = useHotelSearch(hotelData);
  const { createBookRefetch } = useHotelCreateBook();
  const { enqueue } = useSnackbar();
  const { hasSnappPro } = useSnappPro();
  const [showRoomDetail, setShowRoomDetail] = useState(false);
  const [roomId, setRoomId] = useState<number>(0);
  const [tabSelected, setTabSelected] = useState("");
  const { isLoggedIn } = useLoginStatus();
  const { phoneNumber } = useJwtToken();

  const { data: instalmentWallet } = useFetcher({
    enabled: isLoggedIn,
    retry: false,
    queryKey: ["instalmentWallet"],
    queryFn: () => fetchInstalmentWallet()
  });

  const [reviewIds, setReviewIds] = useState([]);
  const [hasError, setHasError] = useState(false);
  const onUsefulnessClick = (id: number) =>
    setReviewIds((previousState) => [
      ...previousState,
      { id, isFetched: isFetched || !hasError }
    ]);

  const { isFetched } = useFetcher({
    enabled: reviewIds.length > 0 && isLoggedIn,
    queryKey: ["usefulnessReview", reviewIds?.[reviewIds?.length - 1]?.id],
    queryFn: ({ queryKey }) =>
      fetchUsefulnessReviewData(Number(queryKey[1]))
        .then(() => setHasError(false))
        .catch((error) => {
          setHasError(true);
          enqueue(error?.response?.message, "error", "one-line");
        })
  });

  const [noticedRoomData, setNoticedRoomData] = useState({
    id: 0,
    isSubmitted: false
  });
  const { data: roomsNotifyData, refetch } = useFetcher({
    enabled: Boolean(phoneNumber),
    queryKey: ["useRoomsNotify", hotelData?.id, noticedRoomData?.id],
    queryFn: ({ queryKey }) =>
      fetchRoomsNotify({
        hotelId: Number(queryKey[1])
      })
  });

  const onNotifyCloseModal = useCallback(
    () =>
      router?.replace(
        {
          pathname: "/[cityTitle]/[hotelTitle]",
          query: {
            ...omit(router?.query, "notify_modal")
          }
        },
        undefined,
        { shallow: true, scroll: false }
      ),
    [router]
  );
  const onNotifyClick = useCallback(
    (roomId: number, isSubmitted: boolean) =>
      isSubmitted || get(router?.query, "notify_modal")
        ? roomsNotifyData?.length >= 15 && !isSubmitted
          ? onNotifyCloseModal()
          : setNoticedRoomData({
              id: roomId,
              isSubmitted
            })
        : router?.replace(
            {
              pathname: "/[cityTitle]/[hotelTitle]",
              query: {
                ...router?.query,
                ...(isSubmitted ? {} : { notify_modal: `open_${roomId}` }),
                ...(phoneNumber ? {} : { login_step: "phone-number" })
              }
            },
            undefined,
            { shallow: true, scroll: false }
          ),
    [onNotifyCloseModal, phoneNumber, roomsNotifyData?.length, router]
  );

  const { isLoading: isNotifyLoading } = useFetcher({
    enabled: Boolean(noticedRoomData?.id) && isLoggedIn,
    queryKey: ["useRoomNotify", noticedRoomData, hotelData?.id],
    queryFn: ({ queryKey }) => {
      const dataArguments = {
        dateFrom: String(get(router?.query, "date_from")),
        hotelId: Number(queryKey[2]),
        roomId: queryKey[1]?.["id"],
        dateTo: String(get(router?.query, "date_to"))
      };
      return noticedRoomData?.isSubmitted
        ? fetchRemoveRoomNotify(dataArguments)
            .then(() => refetch())
            .catch((error) => {
              setHasError(true);
              enqueue(error?.response?.message, "error", "one-line");
            })
        : fetchAddRoomNotify(dataArguments)
            .then(() => {
              onNotifyCloseModal();
              enqueue("درخواست شما با موفقیت ثبت شد", "success", "one-line");
              return refetch();
            })
            .catch((error) => {
              onNotifyCloseModal();
              enqueue(error?.response?.message, "error", "one-line");
            });
    }
  });

  useEffect(() => {
    if (isInvalidCheckin(router.query.date_from, router.query.date_to)) {
      router.replace(
        {
          pathname: "/[cityTitle]/[hotelTitle]",
          query: omit(router.query, ["date_from", "date_to"])
        },
        undefined,
        { shallow: true, scroll: false }
      );
    }
  }, [router]);

  const {
    data: hotelRoomsData,
    isLoading: hotelRoomsLoading,
    status: hotelRoomsStatus,
    isFetching: hotelRoomsFetching
  } = useFetcher({
    enabled:
      hotelData?.status &&
      !isInvalidCheckin(router.query.date_from, router.query.date_to),
    refetchOnMount: true,
    queryKey: [
      "hotelRoomsData",
      hotelData?.id,
      omit(router.query, [
        "modal",
        "login_step",
        "item",
        "no_rooms",
        "notify_modal"
      ])
    ],
    queryFn: ({ queryKey }) => fetchHotelRoomsData(queryKey[1], queryKey[2])
  });

  const {
    handleTrackHotelPage,
    handleVisibleRoomCard,
    handleTrackShowMoreDetail,
    handleTrackShowUnavailableRooms,
    handleTrackVisibleComment
  } = useHotelPageTracking(
    hotelData,
    showRoomDetail,
    hotelRoomsData,
    hotelRoomsLoading
  );

  const hotelRooms = useMemo(
    () =>
      hotelRoomsData?.map?.((room) => ({
        ...room,
        hasSnappProDiscount: room.hasSnappProDiscount && hasSnappPro,
        roomRacks: room?.roomRacks?.map((rack) => ({
          ...rack,
          onClick: () =>
            createBookRefetch({
              hotelId: hotelData?.id,
              roomId: room?.id,
              bookType: room?.bookType,
              availableRooms: rack?.availableRoom,
              dateFrom: rack?.dateFrom,
              dateTo: rack?.dateTo
            })
        })),
        onClick: () => {
          handleTrackHotelPage(room);

          return createBookRefetch({
            hotelId: hotelData?.id,
            roomId: room.id,
            bookNeedConfirm: room.bookNeedConfirm,
            bookType: room.bookType,
            promotion: room.promotion,
            provider: room.provider,
            availableRooms: room.availableRooms
          });
        }
      })) ?? [],

    [createBookRefetch, handleTrackHotelPage, hotelData?.id, hotelRoomsData]
  );
  const { comments, onLoadMore, onLoadFilter, filters, isLoading } =
    useHotelComments({
      hotelId: hotelData?.id
    });
  const { calendar, isCalendarLoading } = useRoomCalendar({
    hotelId: hotelData?.id,
    roomId
  });

  const {
    roomComments,
    onLoadMoreRoomComments,
    onLoadRoomCommentsFilter,
    roomCommentsFilters,
    isRoomCommentsLoading
  } = useHotelRoomComments({
    hotelId: hotelData?.id,
    roomId
  });

  const handleShowRoomDetail = useCallback(
    ({
      roomId,
      tabSelected,
      availableRooms,
      noRooms
    }: {
      roomId: number;
      tabSelected?: string;
      availableRooms?: number;
      noRooms?: number;
    }) => {
      handleTrackShowMoreDetail(
        availableRooms,
        hotelRoomsData.find((room) => room.id === roomId)
      );
      setRoomId(roomId);
      setTabSelected(tabSelected);
      setShowRoomDetail(true);
      onLoadMore();
      return router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            no_rooms: noRooms
          }
        },
        undefined,
        { shallow: true, scroll: false }
      );
    },
    [handleTrackShowMoreDetail, hotelRoomsData, onLoadMore]
  );

  const hotelReserveRateProperties = {
    ...hotelRateData,
    hotelType: hotelData?.accommodation_type
  };

  const { data: userInfo } = useUserInfo();

  const hotelRoomsMinimumPriceRange = useMemo(
    () =>
      numberConvert(numberSeparator(hotelPriceRange?.min), {
        locale: router.locale
      }),
    [hotelPriceRange, router.locale]
  );

  useEffect(() => {
    if (getChannel() === "walkin") {
      router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          channel: "website"
        }
      });
    }
  }, [router]);

  return {
    hotelBreadcrumbsProps: {
      color: "default" as const,
      variant: "default" as const,
      list: [
        {
          title: "رزرو هتل",
          href: getOrigin()
        },
        {
          title: `هتل های ${hotelData?.city_title}`,
          href: {
            pathname: "/[cityTitle]",
            query: pick(router.query, ["cityTitle"])
          },
          onClick: (event: MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault();
            return router.push(
              {
                pathname: "/[cityTitle]",
                query: omit(router.query, "hotelTitle")
              },
              undefined,
              { shallow: true, scroll: false }
            );
          }
        },
        {
          title: `${hotelData?.accommodation_type} ${hotelData?.title} ${hotelData?.city_title}`,
          href: {
            pathname: "/[cityTitle]/[hotelTitle]",
            query: pick(router.query, ["cityTitle", "hotelTitle"])
          }
        }
      ] as Array<BreadcrumbItemType>
    },
    hotelReserveFacilitiesProps: {
      facilities: (hotelData?.facilities ?? []).map((item) => ({
        ...item,
        icon: item.icon === "what.ico" ? "check_box" : item.icon
      })),
      hotelTitle: hotelData?.title,
      hotelType: hotelData?.accommodation_type
    },
    hotelReserveRoomsLayoutProps: {
      cancellation_policy: hotelData?.cancellation_policy,
      hotelId: hotelData?.id,
      isHotelActive: hotelData?.status,
      loading: hotelRoomsLoading || hotelRoomsStatus === "pending",
      roomDetail: {
        roomId,
        showRoomDetail,
        setShowRoomDetail,
        handleShowRoomDetail,
        tabSelected,
        calendar,
        suggestions: hotelRooms
          ?.find((room) => room?.id === roomId && room?.rackIncluded)
          ?.roomRacks?.map((rack) => ({
            date_from: rack?.dateFrom,
            date_to: rack?.dateTo
          })),
        isCalendarLoading,
        isLabelToTop: true,
        hotelRoomsFetching,
        comments: roomComments.map((item) => ({
          ...item,
          helpfulSubmitted: !isLoggedIn || item?.helpfulSubmitted,
          submitSucceed:
            !isLoggedIn || reviewIds?.[reviewIds?.length - 1]?.id === item?.id
              ? isFetched
              : reviewIds?.filter(({ id }) => id === item?.id)?.[0]?.isFetched,
          onUsefulnessClick
        })),
        onLoadFilter: onLoadRoomCommentsFilter,
        filters: roomCommentsFilters,
        onLoadMoreComments: onLoadMoreRoomComments,
        isCommentsLoading: isRoomCommentsLoading
      },
      searchProperties,
      hotelRooms: hotelRooms?.map((room) => ({
        ...room,
        ...(get(router?.query, "date_from") &&
        get(router?.query, "date_from") !== today() &&
        isChannel(["website", "web", "jek"])
          ? {
              notifyData: {
                isOpenModal:
                  get(router?.query, "notify_modal")?.includes(
                    String(room?.id)
                  ) ?? false,
                onCloseModal: onNotifyCloseModal,
                isLimited: roomsNotifyData?.length >= 15,
                isSubmitted: !isEmpty(
                  roomsNotifyData?.find(
                    ({ roomId, dateFrom, dateTo, expireDate, hotelId }) =>
                      roomId === Number(room?.id) &&
                      hotelId === Number(hotelData?.id) &&
                      dateFrom === String(get(router?.query, "date_from")) &&
                      dateTo === String(get(router?.query, "date_to")) &&
                      new Date(expireDate).getTime() > Date.now()
                  )
                ),
                isLoading: isNotifyLoading,
                onclick: onNotifyClick
              }
            }
          : {})
      })),
      cityTitle: hotelData?.city_title,
      hotelTitle: hotelData?.title,
      hotelType: hotelData?.accommodation_type,
      snappPayData: {
        isEligible: instalmentWallet?.isEligible,
        showDownPayment: instalmentWallet?.showDownPayment,
        markup: instalmentWallet?.markup
      },
      roomsFilter: [
        ...(hotelRooms?.length > 0
          ? [
              {
                options: uniqBy(hotelRooms, "roomTypeTitle")?.map(
                  (room) => room?.roomTypeTitle
                ),
                title: "roomType",
                persianTitle: "نوع اتاق",
                iconName: "sensor_door" as const,
                type: "button" as const
              }
            ]
          : []),
        ...(hotelRoomsData?.some((room) => room?.price !== room?.priceOff)
          ? [
              {
                title: "withDiscount",
                persianTitle: "با تخفیف",
                iconName: "percent" as const,
                type: "button" as const
              }
            ]
          : []),
        ...(hotelRoomsData?.some((room) => room?.hasBreakfast)
          ? [
              {
                title: "hasBreakfast",
                persianTitle: "صبحانه",
                iconName: "emoji_food_beverage" as const,
                type: "button" as const
              }
            ]
          : []),
        ...(hotelRoomsData?.some((room) => room?.extraBed)
          ? [
              {
                title: "extraBed",
                persianTitle: "تخت اضافه",
                iconName: "add_circle_outline" as const,
                type: "button" as const
              }
            ]
          : []),
        ...(hotelRoomsData?.some((room) => !room?.availableRooms)
          ? [
              {
                title: "availableRooms",
                toggleList: [
                  {
                    text: "دارای ظرفیت",
                    value: "availableRooms"
                  },
                  {
                    text: "همه اتاق‌ها",
                    value: "all"
                  }
                ],
                type: "toggle" as const,
                onClick: handleTrackShowUnavailableRooms
              }
            ]
          : [])
      ],
      onInView: handleVisibleRoomCard
    },
    hotelReserveMapProps:
      hotelData?.latlon?.[0] && hotelData?.latlon?.[1]
        ? ({
            mapOptions: {
              center: {
                lat: hotelData?.latlon?.[0],
                lng: hotelData?.latlon?.[1]
              }
            },
            marker: {
              lat: hotelData?.latlon?.[0],
              lng: hotelData?.latlon?.[1]
            },
            title: hotelData?.title,
            type: hotelData?.accommodation_type
          } as ReserveMapPropertiesTypes)
        : null,
    hotelReserveDetailsProps: {
      id: hotelData?.id,
      hotelTitle: `${hotelData?.accommodation_type} ${hotelData?.title} ${hotelData?.city_title}`,
      stars: hotelData?.stars,
      latlon: hotelData?.latlon,
      description: hotelData?.description,
      address: hotelData?.address
    },
    hotelReservePoliciesProps: {
      checkinTime: hotelData?.check_in_time,
      checkoutTime: hotelData?.check_out_time,
      hotelTitle: hotelData?.title,
      hotelType: hotelData?.accommodation_type
    },
    hotelReserveCancellationPoliciesProps: cancellationPoliciesData,
    hotelReserveRateProps: {
      ...hotelReserveRateProperties,
      pageTitle:
        getChannel() === "snapp-ir"
          ? `رزرو هتل ${hotelData?.title} (${hotelData?.city_title})- اسنپ`
          : seoData?.title,
      pageDescription:
        getChannel() === "snapp-ir"
          ? `رزرو هتل ${hotelData?.title} به صورت آنلاین با بهترین قیمت، مشاهده تصاویر و دسترسی هتل موقعیت روی نقشه`
          : seoData?.description || seoData?.title
    },
    hotelReserveCommentsProps: {
      hotelTitle: `${hotelData?.accommodation_type} ${hotelData?.title} ${hotelData?.city_title}`,
      comments: comments.map((item) => ({
        ...item,
        helpfulSubmitted: !isLoggedIn || item?.helpfulSubmitted,
        submitSucceed:
          !isLoggedIn || reviewIds?.[reviewIds?.length - 1]?.id === item?.id
            ? isFetched
            : reviewIds?.filter(({ id }) => id === item?.id)?.[0]?.isFetched,
        onUsefulnessClick,
        onVisibleComment: debounce((data: OnVisibleCommentType) => {
          data.inView && handleTrackVisibleComment(data);
        }, 2000)
      })),
      rate: hotelReserveRateProperties,
      filters,
      onLoadMore,
      onLoadFilter,
      isLoading
    },
    hotelReserveRecommendationProps: {
      hotelRecommendation: hotelRecommendations
    },
    hotelReserveCategoriesProps: {
      categories: (hotelData?.categories ?? []).map(({ id, name }) => ({
        id,
        name
      }))
    },
    gallery: hotelData?.gallery ?? [],
    telesaleProperties: {
      funnel: "hotelPage" as const,
      state: hotelData?.title,
      dateFrom: toString(router.query["date_from"]),
      dateTo: toString(router.query["date_to"]),
      userFullName: userInfo?.fullname,
      userMobile: userInfo?.phoneNumber
    },
    hotelSchemaProperties: {
      address: hotelData?.address
        ? numberConvert(hotelData?.address?.replaceAll(".", ""), {
            locale: router.locale
          })
        : "",
      image: getImage({ src: hotelData?.gallery[0]?.media }),
      name: numberConvert(
        `${hotelData?.accommodation_type} ${hotelData?.title} ${hotelData?.city_title}`,
        {
          locale: router.locale
        }
      ),
      priceRange: `شروع قیمت از ${hotelRoomsMinimumPriceRange} تومان`,
      starRating: hotelData?.stars
    },
    isAdChannel: isChannel(["snapp-ir", "ads"])
  };
};
